import { useMemo } from "react";
import { flexRender } from "@tanstack/react-table";
import moment from "moment-timezone";
import { Table as BaseTable, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../../../components/ui/table";
import { cn } from "../../../../../../../lib/utils";

const getGroupKey = (row: any, viewBy: string) => {
    if (viewBy === "date" || viewBy.includes("date")) {
        const rawDate =
            row.original?.date ??
            row.original?.startDate ??
            row.original?.sections?.[0]?.startDate ??
            null;

        return rawDate
            ? moment(rawDate).format("YYYY-MM-DD")
            : "__no_date__";
    }

    if (viewBy === "user") {
        return row.original?.resource?.id ?? "__no_user__";
    }

    if (viewBy === "job") {
        const job =
            row.original?.job ??
            row.original?.sections?.[0]?.job ??
            null;
        return job?.id ?? job?.jobId ?? "__no_job__";
    }

    if (viewBy === "location") {
        const loc =
            row.original?.location ??
            row.original?.sections?.[0]?.location ??
            null;

        return loc?.id ?? loc?.locationId ?? "__no_location__";
    }

    const value =
        row.original?.[viewBy] ??
        row.original?.sections?.[0]?.[viewBy] ??
        null;

    return (
        value?.id ??
        value?.[`${viewBy}Id`] ??
        `__no_${viewBy}__`
    );
}

const groupRows = (rows: any[], viewBy: string) => {
    const groups: { id: string; rowIds: string[] }[] = [];

    rows.forEach((row) => {
        const id = getGroupKey(row, viewBy);
        const index = groups.findIndex((g) => g.id === id);

        if (index === -1) {
            groups.push({
                id,
                rowIds: [row.id],
            });
        } else {
            groups[index].rowIds.push(row.id);
        }
    });

    return groups;
}

const computeRowSpan = (groups: { id: string; rowIds: string[] }[]) => {
    const map = new Map<
        string,
        { span: number; isFirst: boolean }
    >();

    groups.forEach((group) => {
        group.rowIds.forEach((rowId, index) => {
            map.set(rowId, {
                span: index === 0 ? group.rowIds.length : 0,
                isFirst: index === 0,
            });
        });
    });

    return map;
}


function Table({ table, viewBy }: any) {
    const initialRows = table
        .getRowModel()
        .rows
        ?.filter((r: any) => !["unavailable", "open-shifts"].includes(r.original.type));
    const rows = useMemo(() => {
        return [...initialRows].sort((a, b) => {
            const keyA = getGroupKey(a, viewBy);
            const keyB = getGroupKey(b, viewBy);
            return keyA.localeCompare(keyB);
        });
    }, [initialRows, viewBy]);
    const groups = useMemo(() => {
        const result = groupRows(rows, viewBy);
        return result;
    }, [rows, viewBy]);
    const rowSpanInfo = useMemo(() => computeRowSpan(groups), [groups]);

    return (
        <div className="tw-flex tw-flex-col tw-w-full tw-min-h-0" style={{ height: "calc(100vh - 27em)" }}>
            <div className="tw-flex-1 tw-overflow-auto">
                <BaseTable className="tw-w-full tw-caption-bottom tw-text-sm">
                    <TableHeader className="tw-sticky tw-top-0 tw-z-20 tw-bg-muted">
                        {table.getHeaderGroups().map((headerGroup: any) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header: any) => (
                                    <TableHead key={header.id} className="tw-text-center tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>

                    <TableBody>
                        {rows.map((row: any) => (
                            <TableRow key={row.id} className="hover:tw-bg-muted/50">
                                {row.getVisibleCells().map((cell: any) => {
                                    const info = rowSpanInfo.get(row.id);

                                    if (cell.column.id === viewBy) {
                                        if (!info?.isFirst) {
                                            return null;
                                        }

                                        return (
                                            <TableCell
                                                key={cell.id}
                                                rowSpan={info.span}
                                                className="tw-whitespace-nowrap tw-border-b tw-border-r tw-py-3 tw-px-4 tw-text-center tw-align-middle tw-bg-neutral-50 tw-font-medium"
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        );
                                    }

                                    return (
                                        <TableCell
                                            key={cell.id}
                                            className={cn(
                                                "tw-whitespace-nowrap tw-border-b tw-py-3 tw-px-4 tw-text-center tw-align-middle",
                                                info?.isFirst && "tw-border-t tw-border-t-neutral-200"
                                            )}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </BaseTable>
            </div>
        </div>
    );
}

export default Table;
